import { queryOptions } from '@tanstack/react-query';
import { differenceInSeconds } from 'date-fns';
import Cookies from 'js-cookie';
import { apiClient, HTTPError } from 'shared/api-client';
import { ProductTypeSchema, type LoanApplicationId, type WebRecId } from 'shared/schemas/common';
import * as v from 'valibot';

import {
  OffersStatusSchema,
  type OffersStatus,
} from '@/flows/flows/live-offers/-shared/schemas.ts';
import type { CompetitionTileConfig } from '@/pages/CompetitionWinners/CompetitionTile.tsx';
import { PropertyValueCommandOutputSchema } from '@/pages/MyScoopr/lib/schemas/property-value.ts';

import { getRegisterSourceData, type RegisterSourceData } from '../analytics/registerSource.ts';
import { numeric, paginated } from '../schema-utils.ts';

export const updateRegisterSource = async (data: RegisterSourceData) => {
  const { slug, ...custom_data } = data;
  return apiClient.post('bi/api/registersource/', {
    json: { slug, custom_data },
  });
};

export const ExperimentVariantSchema = v.object({
  index: v.number(),
  experiment_id: v.string(),
  cookie: v.string(),
  cookie_name: v.string(),
});

export type ExperimentVariant = v.InferOutput<typeof ExperimentVariantSchema>;

export type GetExperimentVariantArgs = {
  id: string;
};

// Eating raw cookies is not healthy, but sometimes necessary
const rawCookies = Cookies.withConverter({
  read: (value) => value,
  write: (value) => value,
});

export const getExperimentVariant = async (
  args: GetExperimentVariantArgs,
): Promise<ExperimentVariant> => {
  const res = await apiClient.get(`optimize/experiments/${args.id}/get_variant/`).json();

  const data = v.parse(ExperimentVariantSchema, res);

  rawCookies.set(data.cookie_name, data.cookie, {
    domain: '.scoopr.no',
    secure: true,
    sameSite: 'Strict',
    expires: 30,
  });
  void updateRegisterSource(getRegisterSourceData());

  return data;
};

export type AcceptOfferArgs = {
  webRecId: string;
  loanOfferExternalId: number;
};

const AcceptOfferSchema = v.object({
  signing_url: v.string(),
});

export type AcceptOfferResponse = v.InferOutput<typeof AcceptOfferSchema>;

export const acceptOffer = async (args: AcceptOfferArgs): Promise<AcceptOfferResponse> => {
  try {
    const res = await apiClient
      .post(`loans/applications/${args.webRecId}/offers/${args.loanOfferExternalId}/accept/`)
      .json();
    return v.parse(AcceptOfferSchema, res);
  } catch (error) {
    if (error instanceof HTTPError) {
      // Try to find signing_url in the response, it might be there in some cases
      const res = await error.response
        .json()
        .then((data) => v.parse(AcceptOfferSchema, data))
        .catch(() => undefined); // Ignore errors
      if (res) {
        return res;
      }
    }
    console.error('Error accepting offer:', error);
    throw error;
  }
};

export const CompetitionWinnerSchema = v.object({
  name: v.string(),
  age: v.string(),
  city: v.string(),
  meta: v.nullish(v.unknown(), () => undefined),
});

export type CompetitionWinner = v.InferOutput<typeof CompetitionWinnerSchema>;

const CompetitionSchema = v.object({
  slug: v.string(),
  title: v.string(),
  subtitle: v.string(),
  homePageSubtitle: v.nullish(v.string(), () => undefined),
  month: v.string(),
  winners: v.array(CompetitionWinnerSchema),
  meta: v.nullish(v.unknown(), () => undefined),
});

export type Competition = v.InferOutput<typeof CompetitionSchema>;

export const competitionWinnersQuery = () =>
  queryOptions({
    queryKey: ['competition-winners'],
    queryFn: async () => {
      const res = await apiClient.get(`kv_store/api/data/campaign-winners/`).json();
      const schema = v.array(CompetitionSchema);
      return v.parse(schema, res);
    },
  });

export type CompetitionWithConfig = {
  competition: Competition;
  config: CompetitionTileConfig;
};

export const getActiveCompetition = async (
  competitions: Competition[],
): Promise<CompetitionWithConfig | undefined> => {
  const latest = competitions[0];
  const mod = await import('@/pages/CompetitionWinners/config.tsx');
  const config = mod.getConfig(latest.slug);
  const isAfter = differenceInSeconds(config.endDate, new Date()) < 0;
  if (isAfter) {
    return undefined;
  }
  return { competition: latest, config };
};

const TestimonialsSchema = v.object({
  google: v.object({
    rating: v.number(),
    count: v.number(),
  }),
  trustpilot: v.object({
    rating: v.number(),
    count: v.number(),
  }),
  reviews: v.pipe(
    v.array(
      v.object({
        name: v.string(),
        text: v.string(),
        date: v.string(),
      }),
    ),
  ),
});

export type TestimonialsData = v.InferOutput<typeof TestimonialsSchema>;

export const testimonialsQuery = () =>
  queryOptions({
    queryKey: ['testimonials'],
    queryFn: async () => {
      const res = await apiClient.get('kv_store/api/data/testimonials/').json();
      return v.parse(TestimonialsSchema, res);
    },
  });

const SurveyAnswersSchema = v.record(
  v.string(),
  v.object({
    value: v.string(),
  }),
);

const SurveyFinalFeedbackSchema = v.object({
  message: v.string(),
  consents: v.record(v.string(), v.boolean()),
});

export const SurveySchema = v.object({
  id: v.string(),
});

export type Survey = v.InferOutput<typeof SurveySchema>;

export type CreateSurveyArgs = {
  kind_slug: string;
  web_rec_id: WebRecId;
  loan_offer_ada_id?: LoanApplicationId;
  metadata: { flow_slug: string } & Record<string, unknown>;
  status?: 'IN_PROGRESS' | 'COMPLETE' | 'CANCELLED';
  answers?: v.InferInput<typeof SurveyAnswersSchema>;
  final_feedback?: string;
};

export const createSurvey = async (args: CreateSurveyArgs): Promise<Survey> => {
  const response = await apiClient
    .post('feedback/api/surveys/', {
      json: {
        ...args,
        web_rec_id: args.web_rec_id.toString(),
      },
    })
    .json();

  return v.parse(SurveySchema, response);
};

export type UpdateSurveyArgs = {
  id: string;
  answers?: v.InferInput<typeof SurveyAnswersSchema>;
};

export const updateSurvey = async ({ id, ...data }: UpdateSurveyArgs) => {
  await apiClient.patch(`feedback/api/surveys/${id}/`, {
    json: data,
  });
};

export type SubmitSurveyArgs = {
  id: string;
  answers?: v.InferInput<typeof SurveyAnswersSchema>;
  final_feedback?: v.InferInput<typeof SurveyFinalFeedbackSchema>;
};

export const submitSurvey = async ({ id, ...data }: SubmitSurveyArgs) => {
  await apiClient.post(`feedback/api/surveys/${id}/submit/`, {
    json: data,
  });
};

export type CancelSurveyArgs = {
  id: string;
  answers?: v.InferInput<typeof SurveyAnswersSchema>;
  final_feedback?: v.InferInput<typeof SurveyFinalFeedbackSchema>;
};

export const cancelSurvey = async ({ id, ...data }: CancelSurveyArgs) => {
  await apiClient.post(`feedback/api/surveys/${id}/cancel/`, {
    json: data,
  });
};

const ApplicationSimpleSchema = v.object({
  id: v.number(),
  source_flow_slug: v.nullish(v.string(), () => undefined),
  live_offers_flow_version: v.fallback(numeric(), 1),
  product_type: v.nullish(ProductTypeSchema, () => undefined),
  user_answers: v.nullish(v.record(v.string(), v.unknown()), () => {}),
});

export type ApplicationSimple = v.InferOutput<typeof ApplicationSimpleSchema>;

/** For the use in live offers and other anonymous flows */
export const getApplicationSimple = async (webRecId: WebRecId) => {
  const res = await apiClient.get(`loans/applications/${webRecId}/`).json();
  return v.parse(ApplicationSimpleSchema, res);
};

export const getOffersStatus = async (webRecId: WebRecId): Promise<OffersStatus> => {
  const res = await apiClient.get(`loans/applications/${webRecId}/offers_status/`).json();
  return v.parse(OffersStatusSchema, res);
};

const PropertyCommandHistoryEntrySchema = v.object({
  datetime_month_trunc: v.string(),
  created_at: v.string(),
  output_data: PropertyValueCommandOutputSchema,
});

export type PropertyCommandHistoryEntry = v.InferOutput<typeof PropertyCommandHistoryEntrySchema>;

const PropertyCommandHistoryResponseSchema = paginated(PropertyCommandHistoryEntrySchema);

export type PropertyCommandHistoryResponse = v.InferOutput<
  typeof PropertyCommandHistoryResponseSchema
>;

export type GetPropertiesHistoryArgs = {
  page?: number;
};

export const getPropertiesHistory = async ({ page = 1 }: GetPropertiesHistoryArgs = {}) => {
  const res = await apiClient
    .get('properties/commands/history/', { searchParams: { page } })
    .json();
  return v.parse(PropertyCommandHistoryResponseSchema, res);
};

const CreditScoreHistoryOutputSchema = v.object({
  score: v.nullish(v.number(), () => undefined),
  band: v.nullish(v.string(), () => undefined),
  payment_remarks: v.nullish(v.array(v.unknown()), () => undefined),
});

const CreditScoreCommandHistoryEntrySchema = v.object({
  datetime_month_trunc: v.string(),
  created_at: v.string(),
  output_data: v.nullish(CreditScoreHistoryOutputSchema, () => ({
    score: undefined,
    band: undefined,
    payment_remarks: undefined,
  })),
});

export type CreditScoreCommandHistoryEntry = v.InferOutput<
  typeof CreditScoreCommandHistoryEntrySchema
>;

const CreditScoreCommandHistoryResponseSchema = paginated(CreditScoreCommandHistoryEntrySchema);

export type GetCreditScoreHistoryArgs = {
  page?: number;
};

export const getCreditScoreHistory = async ({ page }: GetCreditScoreHistoryArgs = {}) => {
  const res = await apiClient
    .get('creditscore/commands/history/', { searchParams: { page } })
    .json();
  return v.parse(CreditScoreCommandHistoryResponseSchema, res);
};
